import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import AdminDataService from "../services/admin.service";
import api, { setAuthToken } from "../http-common";
import Select from 'react-select';

const ProfileModal = ({ isOpen, email, onSaved, canClose = false, onClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [form, setForm] = useState({
    nom: "", prenom: "", sexe: "", date_naiss: "", nationalite: "", residence: "", tel: ""
  });
  const [busy, setBusy] = useState(false);
  const [subTerrs, setSubTerrs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [subTerrError, setSubTerrError] = useState(null);
  const [countriesError, setCountriesError] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [telValid, setTelValid] = useState(false);
  const [telVerified, setTelVerified] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);

  useEffect(() => {
    const run = async () => {
      try {
        setSubTerrError(null);
        setCountriesError(null);
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email"
        });
        if (token) setAuthToken(token);
        const res = await AdminDataService.getUserNameByEmail(email);
        const p = res?.data || {};
        setForm({
          nom: p.nom || "",
          prenom: p.prenom || "",
          sexe: (p.sexe || "").toUpperCase(),
          date_naiss: p.date_naiss ? String(p.date_naiss).slice(0, 10) : "",
          nationalite: p.nationalite || "",
          residence: p.residence || "",
          tel: p.tel || ""
        });
        try {
          const subRes = await AdminDataService.list('sub-terr');
          const arr = Array.isArray(subRes?.data) ? subRes.data : [];
          setSubTerrs(arr);
        } catch (e) {
          setSubTerrs([]);
          const status = e?.response?.status;
          const detail = e?.response?.data?.error || e?.response?.data?.message;
          setSubTerrError(`Chargement résidences impossible (${status || 'error'}). ${detail || e?.message || ''}`.trim());
        }
        try {
          const publicUrl = process.env.PUBLIC_URL || '';
          const resPays = await fetch(`${publicUrl}/listePays.txt`);
          if (!resPays.ok) {
            throw new Error(`HTTP ${resPays.status}`);
          }
          const txt = await resPays.text();
          const arr = txt.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
          setCountries(arr);
        } catch (e) {
          setCountries([]);
          setCountriesError(`Chargement pays impossible. ${e?.message || ''}`.trim());
        }
      } catch {
        // Ignore load errors, user can still submit
      }
    };
    if (isOpen && email) run();
  }, [isOpen, email, getAccessTokenSilently]);

  const GABON_TEL_REGEX = /^241\d{8}$/; // E.164 without '+' prefix
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'tel') {
      const clean = value.replace(/\s+/g, '');
      const ok = GABON_TEL_REGEX.test(clean);
      setTelValid(ok);
      setTelVerified(false); // reset verification if phone changes
    }
  };

  const sendOtp = async () => {
    if (!telValid) return;
    setOtpBusy(true);
    try {
      //const val = form.tel.replace(/\s+/g, '');
      const val = (form.tel || '').replace(/\D+/g, '');
      //await api.post('/sms/send-otp', null, { params: { val } });
      await api.post('/sms/send-otp', {}, { params: { val } });
      setOtpSent(true);
    } finally {
      setOtpBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpCode.trim()) return;
    setOtpBusy(true);
    try {
      const tel = form.tel.replace(/\s+/g, '');
      const pass = otpCode.trim();
      const { data } = await api.post('/sms/verify-otp', {}, { params: { tel, pass } });
      //await api.post('/sms/verify-otp', null, { params: { tel, pass } });
      if (data?.success) setTelVerified(true); else setTelVerified(false);
    } finally {
      setOtpBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await AdminDataService.updateUserNameByEmail({
        email,
        nom: form.nom.trim(),
        prenom: form.prenom.trim(),
        sexe: form.sexe.trim().toUpperCase(),
        date_naiss: form.date_naiss,
        nationalite: form.nationalite.trim(),
        residence: form.residence.trim(),
        tel: (form.tel || "").toString().trim()
      });
      onSaved && onSaved();
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  const countryOptions = (countries || []).map(p => ({ value: p, label: p }));
  const subTerrOptions = (subTerrs || []).map(st => ({
    value: st.id,
    label: `${st.type_label || st.libelle_type_sub_terr} ${st.libelle_sub_terr}${st.tutelle_label ? ` (${st.tutelle_label})` : ''}`
  }));

  // No close button; cannot be dismissed until valid and saved
  return (
    <>
      <div className="modal fade show" style={{ display: "block" }} role="dialog" aria-modal="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <form onSubmit={onSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">Compléter votre profil</h5>
                {canClose && (
                  <button type="button" className="btn-close" aria-label="Fermer" onClick={() => onClose && onClose()}></button>
                )}
              </div>
              <div className="modal-body">
                {countriesError && (
                  <div className="alert alert-warning" role="alert">
                    {countriesError}
                  </div>
                )}
                {subTerrError && (
                  <div className="alert alert-warning" role="alert">
                    {subTerrError}
                  </div>
                )}
                <div className="mb-2">
                  <label className="form-label">Nom</label>
                  <input className="form-control" name="nom" value={form.nom} onChange={onChange} required />
                </div>
                <div className="mb-2">
                  <label className="form-label">Prénom</label>
                  <input className="form-control" name="prenom" value={form.prenom} onChange={onChange} required />
                </div>
                <div className="mb-2">
                  <label className="form-label">Sexe</label>
                  <select className="form-select" name="sexe" value={form.sexe} onChange={onChange} required>
                    <option value="">—</option>
                    <option value="M">M</option>
                    <option value="F">F</option>
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label">Date de naissance</label>
                  <input className="form-control" type="date" name="date_naiss" value={form.date_naiss} onChange={onChange} required />
                </div>
                <div className="mb-2">
                  <label className="form-label">Nationalité</label>
                  <Select
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Sélectionner la nationalité"
                    options={countryOptions}
                    value={countryOptions.find(o => String(o.value) === String(form.nationalite || '')) || null}
                    onChange={(e) => setForm(prev => ({ ...prev, nationalite: e ? e.value : '' }))}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    noOptionsMessage={() => 'Aucun pays trouvé'}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Résidence</label>
                  <Select
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Sélectionner la résidence"
                    options={subTerrOptions}
                    value={subTerrOptions.find(o => String(o.value) === String(form.residence || '')) || null}
                    onChange={(e) => setForm(prev => ({ ...prev, residence: e ? e.value : '' }))}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    noOptionsMessage={() => 'Aucune localisation trouvée'}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Téléphone</label>
                  <div className="input-group">
                    <span className="input-group-text">+</span>
                    <input className={`form-control${form.tel && !telValid ? ' is-invalid' : ''}`} name="tel" value={form.tel} onChange={onChange} placeholder="241xxxxxxxx" required />
                    <button type="button" className="btn btn-outline-secondary" onClick={sendOtp} disabled={!telValid || otpBusy}>Vérifier</button>
                  </div>
                  <div className="form-text">Format attendu: 241 suivi de 8 chiffres (ex: 241xxxxxxxx)</div>
                  {otpSent && !telVerified && (
                    <div className="mt-2 input-group">
                      <input className="form-control" placeholder="Code OTP reçu" value={otpCode} onChange={(e)=>setOtpCode(e.target.value)} />
                      <button type="button" className="btn btn-outline-primary" onClick={verifyOtp} disabled={otpBusy || !otpCode.trim()}>Confirmer</button>
                    </div>
                  )}
                  {telVerified && <div className="text-success mt-1">Numéro vérifié</div>}
                </div>
              </div>
              <div className="modal-footer">
                {canClose && (
                  <button type="button" className="btn btn-outline-secondary" onClick={() => onClose && onClose()}>
                    Fermer
                  </button>
                )}
                <button className="btn btn-primary" type="submit" disabled={busy || !telVerified}>
                  {busy ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default ProfileModal;